import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ProcessingReportService from "../../../Services/reports/ProcessingReportService";

export function buildTopTitleMetricsLookup(report) {
  const byIsrc = {};
  const byName = {};

  const addEntry = (item) => {
    if (!item || typeof item !== 'object') return;

    const name = String(item.name || item.title || '').trim();
    const isrc = String(item.isrc || item.asset_isrc || '').trim();
    const streams = Number(item.streams);
    const downloads = Number(item.downloads);

    if (!Number.isFinite(streams)) return;

    const metrics = {
      streams,
      downloads: Number.isFinite(downloads) ? downloads : 0,
    };

    if (isrc) {
      byIsrc[isrc] = metrics;
    }
    if (name && !byName[name]) {
      byName[name] = metrics;
    }
  };

  const dbData = report?.database_data || report || {};
  const s3Data = report?.s3_data || {};

  [
    ...(dbData.top_titles || []),
    ...(dbData.top_analysis?.top_titles || []),
    ...(dbData.top_performing_assets || []),
    ...(s3Data.top_analysis?.top_titles || []),
    ...(s3Data.top_performing_assets || []),
  ].forEach(addEntry);

  return { byIsrc, byName };
}

export function resolveTitleMetrics(item, lookup) {
  if (item?.streams !== null && item?.streams !== undefined) {
    const directStreams = Number(item.streams);
    const directDownloads = Number(item.downloads);

    if (Number.isFinite(directStreams)) {
      return {
        streams: directStreams,
        downloads: Number.isFinite(directDownloads) ? directDownloads : 0,
      };
    }
  }

  const isrc = String(item?.isrc || item?.asset_isrc || '').trim();
  const name = String(item?.name || '').trim();

  if (isrc && lookup?.byIsrc?.[isrc]) {
    return lookup.byIsrc[isrc];
  }
  if (name && lookup?.byName?.[name]) {
    return lookup.byName[name];
  }

  return { streams: null, downloads: null };
}

const NAMED_METRICS_FIELDS = {
  dsps: 'top_dsps',
  countries: 'top_countries',
  sale_types: 'sale_types',
};

export function buildNamedMetricsLookup(report, section) {
  const byName = {};
  const field = NAMED_METRICS_FIELDS[section] || section;
  const dbData = report?.database_data || report || {};
  const s3Data = report?.s3_data || {};

  const addEntry = (item) => {
    if (!item || typeof item !== 'object') return;

    const name = String(item.name || '').trim();
    if (!name) return;

    const streams = Number(item.streams);
    if (!Number.isFinite(streams)) return;

    const metrics = {
      streams,
      downloads: Number.isFinite(Number(item.downloads)) ? Number(item.downloads) : 0,
      total_units: item.total_units ?? null,
      streams_percentage: item.streams_percentage ?? null,
      downloads_percentage: item.downloads_percentage ?? null,
    };

    if (!byName[name]) {
      byName[name] = metrics;
    }
  };

  [
    ...(dbData.top_analysis?.[field] || []),
    ...(dbData[field] || []),
    ...(s3Data.top_analysis?.[field] || []),
    ...(s3Data[field] || []),
  ].forEach(addEntry);

  return { byName };
}

export function resolveNamedMetrics(item, lookup) {
  if (item?.streams !== null && item?.streams !== undefined) {
    const directStreams = Number(item.streams);
    const directDownloads = Number(item.downloads);

    if (Number.isFinite(directStreams)) {
      return {
        streams: directStreams,
        downloads: Number.isFinite(directDownloads) ? directDownloads : 0,
        total_units: item.total_units ?? null,
        streams_percentage: item.streams_percentage ?? null,
        downloads_percentage: item.downloads_percentage ?? null,
      };
    }
  }

  const name = String(item?.name || '').trim();
  if (name && lookup?.byName?.[name]) {
    return lookup.byName[name];
  }

  return {
    streams: null,
    downloads: null,
    total_units: item?.total_units ?? null,
    streams_percentage: item?.streams_percentage ?? null,
    downloads_percentage: item?.downloads_percentage ?? null,
  };
}

export function useHasProcessedData(incomeId, reportId, row = null) {
  const hasBackendFlag =
    row?.already_imported != null || row?.has_distribution_data != null;

  const { data: reportResponse } = useQuery({
    queryKey: ["report", reportId],
    queryFn: () => ProcessingReportService.getReportById(reportId),
    enabled: !!reportId && !hasBackendFlag,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (row?.already_imported != null) {
    return Boolean(row.already_imported);
  }
  if (row?.has_distribution_data != null) {
    return Boolean(row.has_distribution_data);
  }

  const report = reportResponse?.data;
  if (!reportId && !incomeId) return false;

  const dbStatistics = report?.database_data?.statistics;
  const reportStatistics = report?.statistics;
  const statistics = dbStatistics || reportStatistics;
  const savedEntries = Number(statistics?.saved_entries);

  return Number.isFinite(savedEntries) && savedEntries > 0;
}

/** Which CSV menu action to show — prefers backend flags from unified API. */
export function resolveCsvAction(row) {
  const canProcess = row?.can_process_csv != null
    ? Boolean(row.can_process_csv)
    : canProcessCsvRow(row, false);
  const canReprocess = row?.can_reprocess_csv != null
    ? Boolean(row.can_reprocess_csv)
    : false;

  if (canReprocess && !canProcess) {
    return {
      type: "reprocess",
      label: "Re-process CSV",
      hint: row?.process_csv_hint || "Re-import clean CSV into the database",
    };
  }

  if (canProcess) {
    return {
      type: "process",
      label: "Process CSV",
      hint: row?.process_csv_hint || "Import clean CSV into the database",
    };
  }

  return {
    type: "none",
    label: "Process CSV",
    hint: row?.process_csv_hint || processCsvDisabledReason(row, true),
  };
}

/** Run after dropdown closes so SweetAlert is not blocked by the menu. */
export function runAfterDropdownClose(callback) {
  if (typeof callback !== "function") return;
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

export function hasCleanCsvReady(row) {
  return Boolean(row?.has_clean_file || row?.report_id || row?.lambda_triggered);
}

/** First-time S3 → DB import (or auto-trigger Lambda when only raw file exists). */
export function canProcessCsvRow(row, hasProcessedData) {
  if (row?.can_process_csv != null) {
    return Boolean(row.can_process_csv);
  }
  if (hasProcessedData) return false;
  return Boolean(row?.can_trigger_lambda || hasCleanCsvReady(row));
}

/** Re-import clean CSV after data is already in the database. */
export function canReprocessCsvRow(row, hasProcessedData) {
  if (row?.can_reprocess_csv != null) {
    return Boolean(row.can_reprocess_csv);
  }
  if (!hasProcessedData) return false;
  return Boolean(row?.can_reprocess ?? row?.has_clean_file ?? row?.report_id);
}

export function processCsvDisabledReason(row, hasProcessedData) {
  if (row?.process_csv_hint) {
    return row.process_csv_hint;
  }
  if (hasProcessedData) {
    return "Data already imported — use Re-process CSV";
  }
  if (!row?.can_trigger_lambda && !hasCleanCsvReady(row)) {
    if (row?.has_invoice_file && !row?.has_raw_file) {
      return "Invoice/PDF uploaded — upload a raw CSV file to process streaming data";
    }
    return "Upload a raw CSV file first";
  }
  return "Import clean CSV into the database";
}

export function ProcessCsvButton({ incomeId, reportId, onProcess, isPending, incomeData, className = "" }) {
  const hasProcessedData = useHasProcessedData(incomeId, reportId);
  const canProcess = canProcessCsvRow(incomeData, hasProcessedData);
  const isDisabled = isPending || !canProcess;

  return (
    <button
      type="button"
      className={`inc-action-btn inc-action-btn--process ${className}`}
      onClick={() => onProcess(incomeId)}
      disabled={isDisabled}
      title={processCsvDisabledReason(incomeData, hasProcessedData)}
    >
      {isPending ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-cogs" />}
      Process
    </button>
  );
}

export function ReprocessCsvButton({ incomeId, reportId, onReprocess, isPending, incomeData, className = "" }) {
  const hasProcessedData = useHasProcessedData(incomeId, reportId);
  const canReprocess = canReprocessCsvRow(incomeData, hasProcessedData);
  if (!canReprocess) return null;

  return (
    <button
      type="button"
      className={`inc-action-btn inc-action-btn--reprocess ${className}`}
      onClick={() => onReprocess(incomeId)}
      disabled={isPending || !canReprocess}
      title="Re-process CSV"
    >
      {isPending ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-repeat" />}
      Re-process
    </button>
  );
}

export function RetryLambdaButton({ incomeId, onRetry, isPending, incomeData, className = "" }) {
  const canRetryLambda = incomeData?.can_retry_lambda ?? false;
  if (!canRetryLambda) return null;

  return (
    <button
      type="button"
      className={`inc-action-btn inc-action-btn--retry ${className}`}
      onClick={() => onRetry(incomeId)}
      disabled={isPending || !canRetryLambda}
      title="Retry Lambda"
    >
      {isPending ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-refresh" />}
      Retry Lambda
    </button>
  );
}

export function ViewReportButton({ incomeId, reportId, onNavigate, className = "" }) {
  const hasProcessedData = useHasProcessedData(incomeId, reportId);

  return (
    <button
      type="button"
      className={`inc-action-btn inc-action-btn--report ${className}`}
      onClick={() => onNavigate(incomeId, reportId)}
      disabled={!hasProcessedData}
      title={hasProcessedData ? "View report" : "Process data first"}
    >
      <i className="fa fa-file-text-o" />
      Report
    </button>
  );
}

export function ComparisonScoreDisplay({ incomeId, reportId }) {
  const { data: reportResponse, isLoading: isReportLoading } = useQuery({
    queryKey: ["report", reportId || incomeId],
    queryFn: () => ProcessingReportService.getReportById(reportId || incomeId),
    enabled: !!(reportId || incomeId),
    staleTime: 0,
    retry: false,
  });

  const report = reportResponse?.data;
  const processingReportData = report?.s3_data || null;
  const dbStatistics = report?.database_data?.statistics;
  const reportStatistics = report?.statistics;
  const statistics = dbStatistics || reportStatistics;
  const dbArtistInfo = report?.database_data?.artist_info || report?.artist_info;

  const comparisonScore = useMemo(() => {
    if (!report || !processingReportData) return null;

    let score = 0;
    const processedData = {
      metadata: processingReportData.processing_metadata,
      breakdown:
        processingReportData.breakdown_counts ||
        processingReportData.statistics?.breakdown_counts,
    };

    const breakdownTotal =
      (processedData?.breakdown?.total_records || 0) +
      (processedData?.breakdown?.dsp_records || 0) +
      (processedData?.breakdown?.territory_records || 0) +
      (processedData?.breakdown?.sale_type_records || 0);
    const backendTotalRecords = statistics?.saved_entries || 0;

    if (breakdownTotal > 0 && backendTotalRecords > 0 && breakdownTotal === backendTotalRecords) {
      score += 2.5;
    }

    const backendRevenueRaw =
      statistics?.database_totals?.total_revenue ?? dbArtistInfo?.total_revenue ?? null;
    const s3RevenueRaw = processedData?.metadata?.total_revenue ?? null;
    const backendRevenue = backendRevenueRaw !== null ? Number(backendRevenueRaw) : null;
    const s3Revenue = s3RevenueRaw !== null ? Number(s3RevenueRaw) : null;
    if (
      backendRevenue !== null &&
      s3Revenue !== null &&
      !Number.isNaN(backendRevenue) &&
      !Number.isNaN(s3Revenue) &&
      Math.abs(backendRevenue - s3Revenue) < 0.01
    ) {
      score += 2.5;
    }

    const backendStreams =
      statistics?.database_totals?.total_streams != null
        ? Number(statistics.database_totals.total_streams)
        : null;
    const s3Streams =
      processedData?.metadata?.total_streams != null
        ? Number(processedData.metadata.total_streams)
        : null;
    if (
      backendStreams !== null &&
      s3Streams !== null &&
      !Number.isNaN(backendStreams) &&
      !Number.isNaN(s3Streams) &&
      backendStreams === s3Streams
    ) {
      score += 2.5;
    }

    const backendDownloads =
      statistics?.database_totals?.total_downloads != null
        ? Number(statistics.database_totals.total_downloads)
        : null;
    const s3Downloads =
      processedData?.metadata?.total_downloads != null
        ? Number(processedData.metadata.total_downloads)
        : null;
    if (
      backendDownloads !== null &&
      s3Downloads !== null &&
      !Number.isNaN(backendDownloads) &&
      !Number.isNaN(s3Downloads) &&
      backendDownloads === s3Downloads
    ) {
      score += 2.5;
    }

    return { score: Math.round(score), total: 10 };
  }, [report, processingReportData, statistics, dbArtistInfo]);

  if (isReportLoading) {
    return <span className="inc-score inc-score--loading"><i className="fa fa-spinner fa-spin" /></span>;
  }

  if (!report || !processingReportData) {
    return <span className="inc-score inc-score--pending">Pending</span>;
  }

  if (comparisonScore && comparisonScore.score !== null && comparisonScore.score !== undefined) {
    const scoreValue = comparisonScore.score;
    let tone = "low";
    if (scoreValue === 10) tone = "high";
    else if (scoreValue >= 7) tone = "mid";
    else if (scoreValue >= 5) tone = "warn";

    return (
      <span className={`inc-score inc-score--${tone}`} title={`Score ${scoreValue}/10`}>
        {scoreValue}/10
      </span>
    );
  }

  return <span className="inc-score inc-score--pending">N/A</span>;
}

export function IncomeStatusBadge({ status }) {
  const isDone = status === "done";
  return (
    <span className={`inc-status inc-status--${isDone ? "done" : "pending"}`}>
      {isDone ? "Done" : "Pending"}
    </span>
  );
}

export function IncomeSourceBadge({ sourceType }) {
  const isYoutube = sourceType === "youtube";
  return (
    <span className={`inc-source inc-source--${isYoutube ? "youtube" : "streaming"}`}>
      {isYoutube ? "YouTube" : "Streaming"}
    </span>
  );
}

export function formatYoutubeMonth(month) {
  if (!month || typeof month !== "string") return "—";
  const match = month.match(/^(\d{4})-(\d{2})/);
  if (!match) return month;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleString(undefined, { month: "long", year: "numeric" });
}

/** Parse a period date string (YYYY-MM-DD…) without timezone drift. */
function parsePeriodDate(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format a single date like "Aug 1, 2026". */
export function formatPeriodDate(value) {
  const date = parsePeriodDate(value);
  if (!date) return value ? String(value) : "";
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** FUGA-style reporting period label, e.g. "August 1, 2026 – August 31, 2026". */
export function formatReportingPeriod(start, end) {
  const startLabel = formatPeriodDate(start);
  const endLabel = formatPeriodDate(end);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel || "—";
}

/**
 * Group YouTube income rows by reporting period so the list mirrors FUGA's
 * platform + reporting-period reporting instead of per-artist rows.
 */
export function groupYoutubeByReportingPeriod(rows) {
  const groups = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row) return;
    const periodStart = row.period_start || row.month || "";
    const periodEnd = row.period_end || "";
    const key = `${periodStart}|${periodEnd}`;

    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        platform: "YouTube",
        period_start: periodStart,
        period_end: periodEnd,
        amount: 0,
        rows: [],
        artistSet: new Set(),
        currencySet: new Set(),
        allDone: true,
      });
    }

    const group = groups.get(key);
    const amount = Number(row.amount);
    group.amount += Number.isFinite(amount) ? amount : 0;

    const artist = row.artist_name || row.title;
    if (artist) group.artistSet.add(String(artist));

    String(row.currencies ?? "")
      .split(/[,\s/]+/)
      .filter(Boolean)
      .forEach((currency) => group.currencySet.add(currency));

    if (row.status !== "done") group.allDone = false;
    group.rows.push(row);
  });

  return Array.from(groups.values())
    .map((group) => ({
      id: group.id,
      platform: group.platform,
      source_type: "youtube",
      period_start: group.period_start,
      period_end: group.period_end,
      amount: group.amount,
      rows: group.rows,
      artistCount: group.artistSet.size,
      artists: Array.from(group.artistSet),
      currency: group.currencySet.size === 1 ? Array.from(group.currencySet)[0] : null,
      status: group.allDone ? "done" : "pending",
    }))
    .sort((a, b) => String(b.period_start).localeCompare(String(a.period_start)));
}

/** Display money in income lists (2 decimal places). */
export function formatIncomeAmount(value, fractionDigits = 2) {
  const raw = value === null || value === undefined || value === "" ? 0 : value;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (Number.isNaN(n)) {
    return (0).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  return n.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}
